import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellOff, Bell, AlertOctagon, FileDown, Undo2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const de = (s) => (s && s.length >= 10 ? `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}` : s || "–");
const fehler = (err, text) => toast.error(err.response?.data?.detail || text);

const pdfLaden = async (r) => {
  const res = await axios.get(`${API}/rechnungen/${r.id}/gesamtfaelligstellung/pdf`, { responseType: "blob" }).catch((e) => fehler(e, "PDF fehlgeschlagen"));
  if (!res) return;
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = `Gesamtfaelligstellung_${r.rechnungsnummer}.pdf`; document.body.appendChild(a); a.click(); a.remove();
};

const Mahnsperre = ({ r, onDone }) => {
  const [form, setForm] = useState(false);
  const [grund, setGrund] = useState("");
  const setzen = async (aktiv) => {
    try {
      await axios.put(`${API}/rechnungen/${r.id}/mahnsperre`, { aktiv, grund: grund.trim() });
      toast.success(aktiv ? "Mahnsperre gesetzt – es wird nicht mehr gemahnt" : "Mahnsperre aufgehoben");
      setForm(false); onDone();
    } catch (err) { fehler(err, "Speichern fehlgeschlagen"); }
  };
  if (r.mahnsperre) return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="mahnsperre-aktiv">
      <div className="text-sm text-amber-900"><BellOff size={14} className="inline mr-1" /><b>Mahnsperre aktiv</b> seit {de(r.mahnsperre_seit)} ({r.mahnsperre_von}) · {r.mahnsperre_grund}</div>
      <Button size="sm" variant="outline" onClick={() => setzen(false)} data-testid="mahnsperre-aufheben-btn"><Bell size={14} className="mr-1" />Aufheben</Button>
    </div>
  );
  if (!form) return <Button size="sm" variant="outline" onClick={() => setForm(true)} data-testid="mahnsperre-open-btn"><BellOff size={14} className="mr-1" />Mahnsperre setzen</Button>;
  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2 w-full" data-testid="mahnsperre-form">
      <div className="text-xs uppercase tracking-wider text-amber-800">Mahnsperre – gilt, bis ihr sie aufhebt</div>
      <Textarea rows={2} value={grund} onChange={(e) => setGrund(e.target.value)} className="bg-white" placeholder="Grund, z. B. Zahlung bis Dezember gestundet" data-testid="mahnsperre-grund-input" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setForm(false)} data-testid="mahnsperre-cancel-btn">Abbrechen</Button>
        <Button size="sm" onClick={() => setzen(true)} disabled={!grund.trim()} data-testid="mahnsperre-submit-btn">Sperre setzen</Button>
      </div>
    </div>
  );
};

const Gesamtfaellig = ({ r, onDone }) => {
  const [v, setV] = useState(null);
  const [frist, setFrist] = useState(14);
  useEffect(() => { axios.get(`${API}/rechnungen/${r.id}/gesamtfaelligstellung`).then((x) => setV(x.data)).catch(() => setV(null)); }, [r.id, r.gesamtfaellig]);
  const gf = r.gesamtfaellig;
  const ausfuehren = async () => {
    if (!window.confirm(`Den gesamten Rest von ${eur(v.restbetrag)} sofort fällig stellen? Der Ratenplan endet damit.`)) return;
    try { await axios.post(`${API}/rechnungen/${r.id}/gesamtfaelligstellung`, { frist_tage: frist }); toast.success("Gesamtfällig gestellt – Schreiben wird geladen"); await pdfLaden(r); onDone(); }
    catch (err) { fehler(err, "Gesamtfälligstellung fehlgeschlagen"); }
  };
  const aufheben = async () => {
    if (!window.confirm("Gesamtfälligstellung zurücknehmen und alten Ratenplan wiederherstellen?")) return;
    try { await axios.delete(`${API}/rechnungen/${r.id}/gesamtfaelligstellung`); toast.success("Alter Ratenplan wiederhergestellt"); onDone(); }
    catch (err) { fehler(err, "Aufheben fehlgeschlagen"); }
  };
  if (gf) return (
    <div className="rounded-md border border-red-300 bg-red-50 p-3 flex flex-wrap items-center justify-between gap-2" data-testid="gesamtfaellig-aktiv">
      <div className="text-sm text-red-900"><AlertOctagon size={14} className="inline mr-1" /><b>Gesamtfällig</b> seit {de(gf.am)}: {eur(gf.betrag)} bis {de(gf.frist)}</div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => pdfLaden(r)} data-testid="gesamtfaellig-pdf-btn"><FileDown size={14} className="mr-1" />Schreiben</Button>
        <Button size="sm" variant="ghost" onClick={aufheben} data-testid="gesamtfaellig-aufheben-btn"><Undo2 size={14} className="mr-1" />Zurücknehmen</Button>
      </div>
    </div>
  );
  if (!v || !v.rueckstand_raten.length) return null;
  return (
    <div className="rounded-md border border-red-200 bg-white p-3 space-y-2" data-testid="gesamtfaellig-box">
      <div className="text-sm text-stone-800"><b>{v.rueckstand_raten.length} Raten überfällig</b> ({eur(v.rueckstand_betrag)}) · Restbetrag gesamt {eur(v.restbetrag)}</div>
      {v.moeglich ? (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm">Zahlungsfrist
            <select value={frist} onChange={(e) => setFrist(Number(e.target.value))} className="ml-2 rounded border border-stone-300 px-2 py-1 text-sm" data-testid="gesamtfaellig-frist">
              {[7, 14, 21, 30].map((t) => <option key={t} value={t}>{t} Tage</option>)}
            </select>
          </label>
          <Button size="sm" variant="destructive" onClick={ausfuehren} data-testid="gesamtfaellig-btn"><AlertOctagon size={14} className="mr-1" />Gesamten Rest fällig stellen</Button>
        </div>
      ) : <div className="text-xs text-stone-500">Gesamtfälligstellung ist erst ab zwei überfälligen Raten möglich.</div>}
      <div className="text-xs text-stone-500">Nur nutzen, wenn eure Ratenvereinbarung bzw. AGB das bei Verzug vorsehen.</div>
    </div>
  );
};

export const MahnsperreBox = ({ rechnung, onDone }) => (
  <div className="mt-3 space-y-2" data-testid="mahnsperre-box">
    <Mahnsperre r={rechnung} onDone={onDone} />
    {rechnung.zahlungsart === "ratenzahlung" && <Gesamtfaellig r={rechnung} onDone={onDone} />}
  </div>
);
